import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Check, Lock, Unlock } from 'lucide-react';
import { StandardCard } from '../styles/standardStyles';
import './TSRLockModal.css';

/**
 * TSRLockModal - Full-screen lock modal shown when previous day's TSR is missed
 * Blocks all application access until TSR is submitted
 *
 * @param {Function} onNavigateToTSR - Callback to navigate to TSR submission page
 * @param {Function} onNavigateToWastage - Callback to navigate to wastage page
 * @param {string} yesterdayDate - Date of missed TSR (YYYY-MM-DD format)
 * @param {number[]} completedSteps - Steps already completed by parent flow state
 * @param {boolean} forceUnlocked - When true, shows unlock animation immediately
 * @param {Function} onUnlockAnimationComplete - Callback fired after unlock animation ends
 */
const TSRLockModal = ({
  onNavigateToTSR,
  onNavigateToWastage,
  yesterdayDate,
  completedSteps: initialCompletedSteps = [],
  forceUnlocked = false,
  onUnlockAnimationComplete,
}) => {
  const [completedSteps, setCompletedSteps] = useState(initialCompletedSteps);
  const [unlocked, setUnlocked] = useState(forceUnlocked);
  const [shaking, setShaking] = useState(false);
  const timeoutIds = useRef([]);
  const unlockCompleteRef = useRef(onUnlockAnimationComplete);
  const unlockCompletionQueuedRef = useRef(false);
  const UNLOCK_ANIMATION_DURATION_MS = 2400;

  const queueTimeout = (callback, delay) => {
    const timeoutId = window.setTimeout(callback, delay);
    timeoutIds.current.push(timeoutId);
  };

  useEffect(() => {
    return () => {
      timeoutIds.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeoutIds.current = [];
      unlockCompletionQueuedRef.current = false;
    };
  }, []);

  useEffect(() => {
    unlockCompleteRef.current = onUnlockAnimationComplete;
  }, [onUnlockAnimationComplete]);

  useEffect(() => {
    setCompletedSteps(Array.isArray(initialCompletedSteps) ? initialCompletedSteps : []);
  }, [initialCompletedSteps]);

  useEffect(() => {
    if (forceUnlocked) {
      setUnlocked(true);

      if (!unlockCompletionQueuedRef.current) {
        unlockCompletionQueuedRef.current = true;
        queueTimeout(() => {
          unlockCompletionQueuedRef.current = false;
          if (typeof unlockCompleteRef.current === 'function') {
            unlockCompleteRef.current();
          }
        }, UNLOCK_ANIMATION_DURATION_MS);
      }
      return;
    }

    unlockCompletionQueuedRef.current = false;
    setUnlocked(false);
  }, [forceUnlocked]);

  const formattedDate = useMemo(() => {
    if (!yesterdayDate) return 'the previous day';
    const date = new Date(`${yesterdayDate}T00:00:00`);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [yesterdayDate]);

  const markStepDone = (stepNumber) => {
    setCompletedSteps((previousSteps) => {
      if (previousSteps.includes(stepNumber)) {
        return previousSteps;
      }
      return [...previousSteps, stepNumber];
    });
  };

  const handleNavigateToWastage = () => {
    markStepDone(1);
    if (typeof onNavigateToWastage === 'function') {
      onNavigateToWastage();
    }
  };

  const handleNavigateToTSR = () => {
    if (unlocked) return;

    markStepDone(1);
    markStepDone(2);
    if (typeof onNavigateToTSR === 'function') {
      onNavigateToTSR();
    }
  };

  const steps = [
    {
      num: 1,
      text: "Record Yesterday's Wastage First",
      onClick: handleNavigateToWastage,
    },
    {
      num: 2,
      text: "Then Submit Yesterday's TSR",
      onClick: handleNavigateToTSR,
    },
  ];

  return (
    <div className="tsr-lock-overlay">
      <AnimatePresence mode="wait">
        {unlocked ? (
          <motion.div
            key="unlocked"
            className="tsr-unlocked-view"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="tsr-unlocked-icon-wrap"
              initial={{ rotate: -20, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 170, damping: 14, delay: 0.15 }}
            >
              {[0, 1, 2].map((index) => (
                <motion.div
                  key={index}
                  className="tsr-unlocked-ring"
                  initial={{ scale: 1, opacity: 0.6 }}
                  animate={{ scale: 2.5 + index * 0.5, opacity: 0 }}
                  transition={{ duration: 1.35, delay: 0.35 + index * 0.18, ease: 'easeOut' }}
                />
              ))}

              <div className="tsr-unlocked-icon-circle">
                <motion.div
                  initial={{ y: 4 }}
                  animate={{ y: [4, -4, 0] }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  <Unlock className="tsr-unlocked-icon" />
                </motion.div>
              </div>
            </motion.div>

            <motion.h1
              className="tsr-unlocked-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.45 }}
            >
              Unlocked
            </motion.h1>

            <motion.p
              className="tsr-unlocked-text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0, duration: 0.45 }}
            >
              All steps completed. You&apos;re good to go!
            </motion.p>

            {Array.from({ length: 12 }).map((_, index) => {
              const angle = (index / 12) * 360;
              const radians = (angle * Math.PI) / 180;

              return (
                <motion.div
                  key={index}
                  className="tsr-unlocked-confetti-dot"
                  style={{ backgroundColor: index % 2 === 0 ? '#86efac' : '#4ade80' }}
                  initial={{ x: 0, y: 0, opacity: 1 }}
                  animate={{
                    x: Math.cos(radians) * 120,
                    y: Math.sin(radians) * 120,
                    opacity: 0,
                    scale: 0,
                  }}
                  transition={{ duration: 1.1, delay: 0.45 + index * 0.04, ease: 'easeOut' }}
                />
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            key="locked"
            className="tsr-lock-shell"
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ scale: 0.9, opacity: 0, y: -30 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div className="tsr-lock-float-wrap">
              <motion.div
                className="tsr-lock-float"
                animate={shaking ? { x: [-8, 8, -6, 6, -3, 3, 0] } : { y: [0, -6, 0] }}
                transition={
                  shaking
                    ? { duration: 0.5 }
                    : { duration: 2, repeat: Infinity, ease: 'easeInOut' }
                }
              >
                <div className="tsr-lock-float-inner">
                  <Lock className="tsr-lock-float-icon" />
                </div>
                <motion.div
                  className="tsr-lock-float-pulse"
                  animate={{ scale: [1, 1.45], opacity: [0.65, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                />
              </motion.div>
            </motion.div>

            <StandardCard className="tsr-lock-modal" hover={false}>
              <motion.div
                className="tsr-lock-topline"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.5, duration: 0.8, ease: 'easeOut' }}
                style={{ transformOrigin: 'left' }}
              />

              <div className="tsr-lock-card-inner">
                <motion.div
                  className="tsr-lock-icon"
                  initial={{ rotate: -15 }}
                  animate={{ rotate: [0, -5, 5, 0] }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <AlertTriangle className="tsr-lock-warning-icon" />
                </motion.div>

                <motion.h2
                  className="tsr-lock-heading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  Yesterday&apos;s TSR Missing
                </motion.h2>

                <motion.p
                  className="tsr-lock-description"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  You did not submit Sales Report for <strong>{formattedDate}</strong>.
                </motion.p>

                <div className="tsr-lock-actions">
                  {steps.map((step, index) => {
                    const done = completedSteps.includes(step.num);
                    return (
                      <motion.button
                        key={step.num}
                        type="button"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.9 + index * 0.15 }}
                        whileHover={!done ? { scale: 1.02, x: 4 } : undefined}
                        whileTap={!done ? { scale: 0.98 } : undefined}
                        onClick={step.onClick}
                        className={`tsr-lock-step-button ${done ? 'is-done' : ''}`}
                      >
                        <span className={`tsr-lock-step-circle ${done ? 'is-done' : ''}`}>
                          {done ? <Check className="tsr-lock-check-icon" /> : step.num}
                        </span>
                        <span className="tsr-lock-step-text">{step.text}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            </StandardCard>

            <motion.div
              className="tsr-lock-badge"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
            >
              <Lock className="tsr-lock-badge-icon" />
              <span>Access locked until completed</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TSRLockModal;
